//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// autopilot-suggestion-invite imports
import { IState, IProps, IContainer } from "./AutopilotSuggestionInvite.d";
import * as s from "./AutopilotSuggestionInvite.styles";
import { getGoalStringsFromGoals, getIconTypeForGoal } from "../AutopilotSuggestionsCard.util";
// other components
import { H2 } from "@ui";
// util
import { ZonedMoment } from "@util/time-zone.util";
import {
  getAbsoluteDateTime,
  getAbsoluteDate,
} from "@clockwise/client-commons/src/util/format-time";
import { logger } from "@util/logger.util";
import { track, TrackingEvents } from "@util/analytics.util";
// state
import { IReduxState } from "@state/reducers/root.reducer";
import { snackBarOpen } from "@actions/snack-bar.actions";
import {
  updateOrgInvite,
  markDefragValueKnobsAsPresented,
  markDefragValueKnobsAsAccepted,
} from "@mutations";
// material-ui
import { Button } from "@material-ui/core";
import { Error, NotInterested, Mail } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
class AutopilotSuggestionInviteComponent extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      sending: false,
      sent: false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public componentDidMount() {
    if (this.props.active) {
      this.markAsPresented();
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (nextProps.active && !this.props.active) {
      this.markAsPresented();
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private onInviteSuccess = () => {
    this.setState({ sending: false, sent: true });
    this.props.snackBarOpen("Invite sent!");
    this.markAsAccepted();
  };

  private onInviteFailure = () => {
    this.setState({ sending: false });
    logger.error("Failed to send invite from Autopilot suggestions");
    this.props.snackBarOpen("Failed to send invite.", "error");
  };

  private sendInvite = () => {
    this.setState({ sending: true });
    updateOrgInvite(
      this.props.relay.environment,
      {
        calendarIds: [this.props.inviteOrganizerKnob.suggestedInvitee],
        orgRelayId: this.props.org.id,
      },
      this.onInviteSuccess,
      this.onInviteFailure,
    );

    // callback
    this.props.onAction();
    track(TrackingEvents.AUTOPILOT_SUGGESTIONS.AUTOPILOT_SUGGESTION_INVITE_CLICKED);
  };

  private onMarkAsPresentedSuccess = () => {
    this.props.onMarkAsPresentedCallback(this.props.inviteOrganizerKnob.knobUrn);
  };

  private onMarkAsPresentedFailure = () => {
    logger.error("Failed to mark autopilot invite suggestion as presented");
  };

  private markAsPresented = () => {
    markDefragValueKnobsAsPresented(
      this.props.relay.environment,
      {
        knobUrns: [this.props.inviteOrganizerKnob.knobUrn],
        orgRelayId: this.props.org.id,
      },
      this.onMarkAsPresentedSuccess,
      this.onMarkAsPresentedFailure,
    );
  };

  private onMarkAsAcceptedFailure = () => {
    logger.error("Failed to mark autopilot invite suggestion as accepted");
  };

  private markAsAccepted = () => {
    markDefragValueKnobsAsAccepted(
      this.props.relay.environment,
      {
        knobUrns: [this.props.inviteOrganizerKnob.knobUrn],
        orgRelayId: this.props.org.id,
      },
      () => null,
      this.onMarkAsAcceptedFailure,
    );
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private renderInviteButton = (person: ISchema.IPerson | null) => {
    const { classes, inviteOrganizerKnob } = this.props;

    const displayName =
      person && person.profile ? person.profile.givenName : inviteOrganizerKnob.suggestedInvitee;

    return (
      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={this.sendInvite}
        classes={{ root: classes.button }}
        disabled={this.state.sending || this.state.sent}
      >
        <Mail {...s.buttonIcon} />
        <div>{this.state.sent ? `Sent` : `Invite ${!!displayName ? displayName : ""}`}</div>
      </Button>
    );
  };

  private renderPain = () => {
    const { classes, inviteOrganizerKnob } = this.props;
    const goalStrings = [...new Set(getGoalStringsFromGoals(inviteOrganizerKnob.affectedGoals))];

    return (
      <div className={classes.painContainer}>
        {goalStrings.map((goal, i) => {
          return (
            <div key={`${goal.goal}-${i}`} className={classes.warning}>
              {getIconTypeForGoal(goal.goal) === "error" ? (
                <Error {...s.warningIcon} />
              ) : (
                <NotInterested {...s.warningIcon} />
              )}
              <span>{goal.message}</span>
            </div>
          );
        })}
      </div>
    );
  };

  private renderEvent = () => {
    const { classes, referencedEvents, inviteOrganizerKnob } = this.props;
    // only show one event for now
    const event = referencedEvents.find(
      (e) => e.eventKey.externalEventId === inviteOrganizerKnob.affectedEventIds[0],
    );
    if (!event) {
      logger.error("cannot find referenced event for invite knob");
      return "";
    }

    const startTime = new ZonedMoment(event.startTime.millis);
    const endTime = new ZonedMoment(event.endTime.millis);

    const formattedDate = event.isAllDay
      ? startTime.format("YYYY-MM-DD") === endTime.format("YYYY-MM-DD")
        ? getAbsoluteDate(endTime, undefined, true)
        : getAbsoluteDate(startTime, endTime, true)
      : getAbsoluteDateTime(startTime, endTime);

    return (
      <div className={classes.event}>
        <div className={classes.orangeBar}></div>
        <div>
          <div className={classes.title}>{event.title}</div>
          <div className={classes.date}>{formattedDate}</div>
        </div>
      </div>
    );
  };

  public render() {
    const { classes, inviteOrganizerKnob } = this.props;

    const person =
      inviteOrganizerKnob.suggestedInviteePerson.__typename !== "GraphEntityError"
        ? inviteOrganizerKnob.suggestedInviteePerson
        : null;

    const displayName =
      person && person.profile
        ? `${person.profile.givenName} ${person.profile.familyName}`
        : inviteOrganizerKnob.suggestedInvitee;

    return (
      <div className={classes.container}>
        <H2 style={s.styles.header}>Invite {displayName} to improve the time of this event</H2>
        {this.renderEvent()}
        {this.renderPain()}
        {this.renderInviteButton(person)}
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(AutopilotSuggestionInviteComponent);

//////////////////
// WITH STYLES
//////////////////
const AutopilotSuggestionInviteStyled = withStyles(s.styles)(reduxContainer);

//////////////////
// RELAY
//////////////////
import { fragments } from "./AutopilotSuggestionInvite.gql";
export const AutopilotSuggestionInvite = createFragmentContainer<IContainer>(
  AutopilotSuggestionInviteStyled,
  fragments,
);
